import { DASH, formatDate, formatMoney } from './billDetailsUtils';

/**
 * What a trail row records. The service stringifies its enums, so these are capitalised — comparing
 * against `'edit'` matches nothing and fails silently, which is the whole reason they are named here.
 */
export const BILL_EDIT_KIND = {
  edit: 'Edit',
  restore: 'Restore',
  handEntry: 'HandEntry'
};
export const BILL_EDIT_OP = {
  added: 'Added',
  changed: 'Changed',
  removed: 'Removed'
};

/** Paths whose value is an amount, and paths whose value is a date. Matched on the leaf. */
const MONEY_LEAVES = ['amount', 'previousBalance', 'subtotal', 'totalAmountDue'];

/**
 * Format a recorded value the way the rest of the bill is formatted.
 *
 * The trail stores values canonical — ISO dates, invariant decimals — precisely so they are not
 * frozen into whichever locale wrote them, which means the shaping has to happen here. There is no
 * kind on the wire, so it is taken from the path: the same field name the bill renders elsewhere.
 */
export function formatEditValue(path, value) {
  if (value === null || value === undefined || value === '') return DASH;
  const leaf = String(path || '').split('.').pop();
  if (/date$/i.test(leaf)) return formatDate(value);
  if (MONEY_LEAVES.some(name => name.toLowerCase() === leaf.toLowerCase())) return formatMoney(Number(value));
  return String(value);
}

/**
 * The one-line summary a collapsed row shows: who, and how much moved.
 *
 * `changeCount` is read rather than `changes.length` because a row is counted when it is written and
 * the panel may be holding a page that does not carry every change back.
 */
export function describeEdit(entry) {
  var _entry$changeCount;
  const who = (entry === null || entry === void 0 ? void 0 : entry.editedByName) || 'Someone';
  if ((entry === null || entry === void 0 ? void 0 : entry.kind) === BILL_EDIT_KIND.restore) return "".concat(who, " restored the original");

  // A failed read entered by hand (LB-6095): what changed, if anything, is listed beneath like any edit's.
  if ((entry === null || entry === void 0 ? void 0 : entry.kind) === BILL_EDIT_KIND.handEntry) return "".concat(who, " entered this bill by hand");
  const count = (_entry$changeCount = entry === null || entry === void 0 ? void 0 : entry.changeCount) !== null && _entry$changeCount !== void 0 ? _entry$changeCount : 0;
  return "".concat(who, " changed ").concat(count, " ").concat(count === 1 ? 'field' : 'fields');
}

/** How one change reads: what it was, and what it is now. */
export function describeChange(change) {
  const from = formatEditValue(change === null || change === void 0 ? void 0 : change.path, change === null || change === void 0 ? void 0 : change.oldValue);
  const to = formatEditValue(change === null || change === void 0 ? void 0 : change.path, change === null || change === void 0 ? void 0 : change.newValue);
  if ((change === null || change === void 0 ? void 0 : change.op) === BILL_EDIT_OP.added) return {
    from: null,
    to
  };
  if ((change === null || change === void 0 ? void 0 : change.op) === BILL_EDIT_OP.removed) return {
    from,
    to: null
  };
  return {
    from,
    to
  };
}